/**
 * RoutinePicker — reusable multi-select picker for roros_routines.
 *
 * Features:
 *  - Searchable dropdown (by title, case-insensitive)
 *  - Domain pill per routine option
 *  - Status pill for non-active routines
 *  - Filtered to active routines by default; "Show archived" toggle reveals others
 *  - Selected entries rendered as removable chips
 *  - Stale entries (archived or unknown UUID): yellow border + "(archived)"/"(unknown)" badge
 *  - Empty state with link to /roros/artifacts/routines
 *  - Keyboard-accessible (Enter/Space on chip to remove, Escape to close dropdown)
 *
 * Usage:
 *   <RoutinePicker
 *     selected={state.routineOwnership}
 *     onChange={(entries) => setState(s => ({ ...s, routineOwnership: entries }))}
 *   />
 */

import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ChevronDown, Search, AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch/Switch';
import { Badge } from '@/components/ui/Badge';
import { t } from '@/lib/i18n';
import { useRorosRoutines } from '@/hooks/roros/artifacts';
import { useRorosDomains } from '@/hooks/roros/domains';
import type { RorosRoutineArtifactDTO } from '@/types/roros/artifacts';
import type { RorosDomainDTO } from '@/types/roros/domains';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RoutineOwnershipEntry {
  /** Absent on legacy category-based entries — those are passed through unchanged. */
  routineId?: string;
  routineName?: string | null;
  archived?: boolean;
  authority?: string;
  [key: string]: unknown;
}

interface Props {
  /** Currently selected routine entries (may include archived/missing entries from existing data) */
  selected: RoutineOwnershipEntry[];
  /** Called with updated entries whenever the selection changes */
  onChange: (entries: RoutineOwnershipEntry[]) => void;
  /** Optional: disable the picker (e.g. when parent is submitting) */
  disabled?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getChipLabel(entry: RoutineOwnershipEntry, routinesById: Map<string, RorosRoutineArtifactDTO>): string {
  if (!entry.routineId) return entry.routineName ?? '(unknown)';
  const routine = routinesById.get(entry.routineId);
  if (routine) return routine.title;
  if (entry.routineName) return entry.routineName;
  return entry.routineId;
}

/**
 * Bug B fix: trust only the backend's `archived` flag.
 * The backend sets `archived: true` for both archived and deleted/not-found routines,
 * so client-side `routinesById` lookup is not a reliable staleness signal — especially
 * on first render before the routines list has loaded.
 */
function isStale(entry: RoutineOwnershipEntry): boolean {
  return entry.archived === true;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function RoutinePicker({ selected, onChange, disabled = false }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const pickerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // ── Data fetching ─────────────────────────────────────────────────────────

  // Bug A fix: backend Zod schema caps pageSize at 100; '200' triggered a 400 → empty dropdown.
  const routinesQuery = useRorosRoutines({ pageSize: '100' });
  const domainsQuery = useRorosDomains();

  const allRoutines: RorosRoutineArtifactDTO[] = useMemo(
    () => (routinesQuery.data ?? []) as RorosRoutineArtifactDTO[],
    [routinesQuery.data],
  );

  const domainsById = useMemo<Map<string, RorosDomainDTO>>(() => {
    const map = new Map<string, RorosDomainDTO>();
    for (const d of (domainsQuery.data ?? []) as RorosDomainDTO[]) {
      if (d.id) map.set(d.id, d);
    }
    return map;
  }, [domainsQuery.data]);

  const routinesById = useMemo<Map<string, RorosRoutineArtifactDTO>>(() => {
    const map = new Map<string, RorosRoutineArtifactDTO>();
    for (const r of allRoutines) map.set(r.id, r);
    return map;
  }, [allRoutines]);

  // ── Dropdown options ──────────────────────────────────────────────────────

  // Bug C fix: only include entries that actually have a routineId in the set.
  // Category-shaped entries { category, authority } have no routineId and must not
  // pollute the dropdown exclusion set.
  const selectedIds = useMemo(
    () => new Set(selected.filter((s) => typeof s.routineId === 'string').map((s) => s.routineId as string)),
    [selected],
  );

  // Legacy category entries — no routineId — passed through in state/save, not rendered as chips.
  const hasLegacyEntries = useMemo(
    () => selected.some((e) => !e.routineId),
    [selected],
  );

  const options = useMemo<RorosRoutineArtifactDTO[]>(() => {
    return allRoutines.filter((r) => {
      if (selectedIds.has(r.id)) return false;
      if (!showArchived && r.status !== 'active') return false;
      if (search && !r.title.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [allRoutines, selectedIds, showArchived, search]);

  // ── Click-outside close ───────────────────────────────────────────────────

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setPickerOpen(false);
      }
    }
    if (pickerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [pickerOpen]);

  // Focus search when picker opens
  useEffect(() => {
    if (pickerOpen) searchRef.current?.focus();
  }, [pickerOpen]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleAdd(routine: RorosRoutineArtifactDTO) {
    onChange([...selected, { routineId: routine.id }]);
    setSearch('');
    // Keep picker open for batch-adding
  }

  function handleRemove(routineId: string) {
    onChange(selected.filter((e) => e.routineId !== routineId));
  }

  function handleToggle() {
    setPickerOpen((open) => !open);
    setSearch('');
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  const hasRoutinesInOrg = allRoutines.length > 0;

  return (
    <div className="space-y-3">
      {/* ── Legacy category entries banner (Bug C fix) ───────────────────── */}
      {/*
       * Category-shaped entries { category, authority } were once stored in
       * routine_ownership but no longer map to a real routine. They are preserved
       * in state and saved unchanged so no data is lost, but we cannot render
       * them as meaningful chips. Migration 437 will clean them up in production.
       */}
      {hasLegacyEntries && (
        <div
          className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200"
          data-testid="routine-picker-legacy-banner"
        >
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{t('This role has legacy category-based routine bindings that cannot be displayed here. They will be preserved on save and can be cleaned up via a data migration.')}</span>
        </div>
      )}

      {/* ── Selected chips ─────────────────────────────────────────────────── */}
      {/*
       * Bug C fix: only render entries that have a routineId. Category entries
       * (no routineId) cause React key collisions and "(unknown)" labels.
       */}
      {selected.some((e) => typeof e.routineId === 'string' && e.routineId.length > 0) && (
        <ul className="flex flex-wrap gap-2" aria-label={t('Selected routines')}>
          {selected
            .filter((entry): entry is RoutineOwnershipEntry & { routineId: string } =>
              typeof entry.routineId === 'string' && entry.routineId.length > 0,
            )
            .map((entry) => {
              const stale = isStale(entry);
              const label = getChipLabel(entry, routinesById);
              const domainId = routinesById.get(entry.routineId)?.domainId;
              const domainName = domainId ? domainsById.get(domainId)?.name : null;

              return (
                <li
                  key={entry.routineId}
                  className={[
                    'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm border',
                    stale
                      ? 'border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-950 dark:text-amber-200'
                      : 'border-border bg-muted text-foreground',
                  ].join(' ')}
                  data-testid={`routine-chip-${entry.routineId}`}
                >
                  <span className="font-medium truncate max-w-[200px]">{label}</span>

                  {/* Domain pill */}
                  {domainName && (
                    <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                      {domainName}
                    </Badge>
                  )}

                  {/* Stale badge (archived flag set by backend) */}
                  {stale && (
                    <span className="flex items-center gap-0.5 text-[10px] text-amber-700 dark:text-amber-300">
                      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                      {t('(archived)')}
                    </span>
                  )}

                  {/* Remove button */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemove(entry.routineId)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRemove(entry.routineId);
                      }
                    }}
                    aria-label={`${t('Remove')} ${label}`}
                    className="ml-0.5 rounded-full h-5 w-5 p-0"
                    disabled={disabled}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </Button>
                </li>
              );
            })}
        </ul>
      )}

      {/* ── Add routine button + dropdown ──────────────────────────────────── */}
      <div className="relative" ref={pickerRef}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleToggle}
          disabled={disabled}
          aria-expanded={pickerOpen}
          aria-haspopup="listbox"
          data-testid="routine-picker-toggle"
          className="flex items-center gap-1"
        >
          {t('Add routine')}
          <ChevronDown className="h-3.5 w-3.5 ml-0.5" aria-hidden="true" />
        </Button>

        {pickerOpen && (
          <div
            role="dialog"
            aria-label={t('Select routines')}
            className="absolute z-50 mt-1 w-80 rounded-md border border-border bg-background shadow-lg"
          >
            {/* Search */}
            <div className="flex items-center border-b border-border px-3 py-2">
              <Search className="h-4 w-4 text-muted-foreground mr-2 shrink-0" aria-hidden="true" />
              <input
                ref={searchRef}
                type="text"
                placeholder={t('Search routines…')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && setPickerOpen(false)}
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                aria-label={t('Search routines')}
              />
            </div>

            {/* Show archived toggle */}
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-border">
              <span className="text-xs text-muted-foreground">{t('Show archived')}</span>
              <Switch
                checked={showArchived}
                onChange={() => setShowArchived((v) => !v)}
              />
            </div>

            {/* Options list */}
            <ul
              role="listbox"
              aria-label={t('Available routines')}
              className="max-h-60 overflow-y-auto py-1"
            >
              {routinesQuery.isLoading && (
                <li className="px-3 py-2 text-sm text-muted-foreground">{t('Loading…')}</li>
              )}

              {!routinesQuery.isLoading && !hasRoutinesInOrg && (
                <li className="px-3 py-4 text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    {t('No routines yet — create one in Routines')}
                  </p>
                  <Link
                    to="/roros/artifacts/routines"
                    className="text-xs text-primary underline"
                    onClick={() => setPickerOpen(false)}
                  >
                    {t('Go to Routines')}
                  </Link>
                </li>
              )}

              {!routinesQuery.isLoading && hasRoutinesInOrg && options.length === 0 && (
                <li className="px-3 py-2 text-sm text-muted-foreground">
                  {search ? t('No routines match your search') : t('All routines already added')}
                </li>
              )}

              {options.map((routine) => {
                const domainName = routine.domainId
                  ? domainsById.get(routine.domainId)?.name
                  : null;
                return (
                  <li
                    key={routine.id}
                    role="option"
                    aria-selected={false}
                    className="flex items-center justify-between px-3 py-2 hover:bg-accent cursor-pointer gap-2 text-sm"
                    onClick={() => handleAdd(routine)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleAdd(routine);
                      }
                    }}
                    tabIndex={0}
                    data-testid={`routine-option-${routine.id}`}
                  >
                    <span className="truncate flex-1 font-medium">{routine.title}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      {domainName && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                          {domainName}
                        </Badge>
                      )}
                      {routine.status !== 'active' && (
                        <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">
                          {routine.status}
                        </Badge>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* ── Helper text ────────────────────────────────────────────────────── */}
      <p className="text-xs text-muted-foreground">
        {t('Routines this role is responsible for executing.')}
        {' '}
        {!hasRoutinesInOrg && !routinesQuery.isLoading && (
          <Link
            to="/roros/artifacts/routines"
            className="underline text-primary"
          >
            {t('Create a routine')}
          </Link>
        )}
      </p>
    </div>
  );
}
